import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useRelay } from '../../../hooks/useRelay';
import type { RoomView } from '../../../types/room';
import { actorTeam, playerOf, describe, RULES_VERSION, GAME_NAME } from '../../../games/capture-the-flag/capture-the-flag';
import type { CtfAction, Team } from '../../../games/capture-the-flag/types';
import { applyRoomAction, botMove, replayText, restartCtfRoomGame, loadIntoRoom, takeBack, rollbackPoints, requestRollback, answerRollback, CTF_ROOM_DEFAULT_CONFIG } from '../../../games/capture-the-flag/room';
import type { CtfRoomConfig, CtfRoomState } from '../../../games/capture-the-flag/room';
import { positionText } from '../../../games/capture-the-flag/playground/edit';
import CtfGameScreen from './CtfGameScreen';
import { act as leagueAct, leagueToken } from '../../../leagues/api';
import { leagueOfRoom, leagueUnitType } from '../../room/leagueRoom';

interface Props {
  slug: string;
  room: RoomView;
  myId: string;
  isLeader: boolean;
  initialState: CtfRoomState | null;
  onFinish: (result?: { winner?: string; how?: string }) => void;
  onResult?: (result: { winner?: string; how?: string } | null) => void;   // the game has ended (host's browser); null: a new one started in place
}

const HOW: Record<string, string> = { flag: 'Flag home', mercy: 'Mercy rule', domination: 'Domination', resign: 'Resigned', prison: 'Whole army in prison' };
// What the room's "tonight" list records for a finished game.
function resultFor(g: CtfRoomState['game'], names: Record<Team, string>): { winner?: string; how?: string } | undefined {
  if (!g.result) return undefined;
  return { winner: g.result.winner ? names[g.result.winner] : 'Draw', how: `${HOW[g.result.reason] ?? g.result.reason} in game round ${g.result.rounds}` };
}

const TEAMS: Team[] = ['A', 'B'];
const SPEEDS = [{ label: 'Slow', ms: 700 }, { label: 'Normal', ms: 300 }, { label: 'Fast', ms: 80 }];
export default function CaptureTheFlagRoomGame({ slug, room, myId, isLeader, initialState, onFinish, onResult }: Props) {
  const { state, startRelay, sendAction, sync } = useRelay<CtfRoomState>(slug);
  const started = useRef(false);
  const [speed, setSpeed] = useState(1);
  const [copied, setCopied] = useState<'idle' | 'done' | 'failed'>('idle');
  const [showRules, setShowRules] = useState(false);
  const [hostTool, setHostTool] = useState<'none' | 'restart' | 'load'>('none');
  const [loadText, setLoadText] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [rollbackOpen, setRollbackOpen] = useState(false);
  const [rollbackTo, setRollbackTo] = useState<number | null>(null);
  // Where a rollback can go (worked out only while the rollback panel is open).
  const points = useMemo(() => (rollbackOpen && state && !state.fromPosition ? rollbackPoints(state) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rollbackOpen, state?.tokens.length, state?.seed]);
  useEffect(() => {
    if (!isLeader || !initialState || started.current) return;
    started.current = true;
    startRelay('capture-the-flag', initialState);
  }, [isLeader, initialState, startRelay]);

  // Bots: the host's browser plays one bot action per state, after a short pause so people can follow.
  const botIds = useMemo(() => new Set(room.participants.filter(p => p.type === 'bot').map(p => p.id)), [room.participants]);
  useEffect(() => {
    if (!isLeader || !state || state.game.phase !== 'playing') return;
    const team = actorTeam(state.game);
    if (!team || !botIds.has(playerOf(state.game, team).id)) return;
    const timer = window.setTimeout(() => {
      const action = botMove(state, botIds);
      if (!action) return;
      const next = applyRoomAction(state, action);
      if (next !== state) sendAction(action, next);
    }, SPEEDS[speed].ms);
    return () => window.clearTimeout(timer);
  }, [state, isLeader, botIds, speed, sendAction]);

  // Tell the room when the game ends, so the result survives however people go back to the room.
  const res = state?.game.result;
  const ended = res ? `${res.reason}:${res.winner}:${res.rounds}:${state?.seed}` : '';
  useEffect(() => {
    if (!ended || !isLeader || !onResult || !state) return;
    const sg = state.game;
    const demo = !!(room.config as CtfRoomConfig).demo;
    const r = resultFor(sg, demo ? { A: 'Blue', B: 'Red' } : { A: playerOf(sg, 'A').name, B: playerOf(sg, 'B').name });
    if (r) onResult(r);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ended, isLeader]);
  // A rematch, restart or loaded game while the room still holds the last result: the game is on again.
  const playingAgain = !!state && !state.game.result && !!room.result;
  useEffect(() => {
    if (playingAgain && isLeader && onResult) onResult(null);
  }, [playingAgain, isLeader, onResult]);

  // A league game: send its replay to the league once the result is in (each player's browser tries a
  // few times, since the result may reach the league a moment later; the first replay counts).
  useEffect(() => {
    const lg = (room.config as { league?: { leagueId: string; gameId: string } }).league;
    if (!ended || !lg || !state || state.fromPosition || !leagueToken(lg.leagueId)) return;
    const replay = replayText(state);
    // Not cancelled when the game screen closes: going back to the league right away still sends it.
    let stop = false;
    [1500, 6000, 20000].forEach(ms => window.setTimeout(() => {
      if (stop) return;
      leagueAct(lg.leagueId, 'replay', { gameId: lg.gameId, replay })
        .then(L => { if (L.rounds.some(r => r.games.some(x => x.id === lg.gameId && x.hasReplay))) stop = true; })
        .catch(() => undefined);
    }, ms));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ended]);

  if (!state) {
    return <div className="min-h-full bg-gray-950 text-gray-300 p-10">Loading {GAME_NAME}…</div>;
  }

  const g = state.game;
  const participantIds = new Set(room.participants.map(p => p.id));
  const rc: CtfRoomConfig = { ...CTF_ROOM_DEFAULT_CONFIG, ...(room.config as CtfRoomConfig) };
  // A league game is played once: no rematch, restart or loading another game.
  const league = (room.config as { league?: { leagueId: string } }).league ?? null;
  const leagueInfo = leagueOfRoom(room);
  // You play a team if it's yours; a host playing alone (playtest mode) or running a demo plays both.
  const controls = (t: Team) => {
    const p = playerOf(g, t);
    return (rc.demo && isLeader) || p.id === myId || (isLeader && !participantIds.has(p.id));
  };
  const playerIds = new Set(g.players.map(p => p.id));
  const audience = room.participants.filter(p => p.type !== 'bot' && (p.type === 'spectator' || !playerIds.has(p.id) || rc.demo) && !p.isLeader).map(p => p.name);
  const isBot = (t: Team) => botIds.has(playerOf(g, t).id);

  const dispatch = (a: CtfAction) => {
    const next = applyRoomAction(state, a);
    if (next !== state) sendAction(a, next);
  };
  const mine = (t: Team) => controls(t) && !isBot(t);
  const turnNow = g.phase === 'playing' ? actorTeam(g) : null;
  // Take-backs: the player to act, within their own activation, back to its start or its last dice
  // roll (moving units around to try things is free; a roll shows something new, so it isn't).
  const canTakeBack = !!turnNow && mine(turnNow) && state.freeFrom !== undefined && state.tokens.length > state.freeFrom;
  const takeBackMoves = (all: boolean) => {
    const next = takeBack(state, all);
    if (next) sendAction({ type: all ? 'reset-activation' : 'undo-step' }, next);
  };
  // Rollbacks: back to the start of any activation, when both players agree (at once against a bot,
  // or when you play both sides).
  const myTeams = TEAMS.filter(mine);
  const otherPerson = TEAMS.find(t => !isBot(t) && !mine(t));
  const rb = state.rollback ?? null;
  const rbMine = !!rb && rb.by === myId;
  // By default, back to the start of your own last activation.
  const defaultPoint = points.find(pt => myTeams.includes(pt.team)) ?? points[0];
  const chosenPoint = points.find(pt => pt.at === rollbackTo) ?? defaultPoint;
  const askRollback = () => {
    if (!chosenPoint) return;
    setRollbackOpen(false);
    setRollbackTo(null);
    const asked = requestRollback(state, myId, chosenPoint);
    if (!otherPerson) sendAction({ type: 'rollback' }, answerRollback(asked, true));
    else sendAction({ type: 'rollback-request' }, asked);
  };
  const answer = (accept: boolean) => sendAction({ type: accept ? 'rollback-accept' : 'rollback-decline' }, answerRollback(state, accept));
  const callOff = () => sendAction({ type: 'rollback-cancel' }, { ...state, rollback: null });
  const names: Record<Team, string> = rc.demo ? { A: 'Blue', B: 'Red' } : { A: playerOf(g, 'A').name, B: playerOf(g, 'B').name };

  // Host tools: a fresh game with the same settings and sides, or a replay / position loaded into the room.
  const restart = () => {
    setHostTool('none');
    sendAction({ type: 'restart' }, restartCtfRoomGame(rc, g, Date.now()));
  };
  const load = () => {
    try {
      const next = loadIntoRoom(loadText, g.players);
      setLoadError(null);
      setLoadText('');
      setHostTool('none');
      sendAction({ type: 'load' }, next);
    } catch (e) {
      setLoadError((e as Error).message);
    }
  };

  // A game loaded from a set-up position has no replay from the start: copy the position instead.
  const shareText = () => state.fromPosition ? positionText(g, state.seed) : replayText(state);
  const shareLabel = state.fromPosition ? 'position' : 'replay';
  const copyReplay = async () => {
    const text = shareText();
    try { await navigator.clipboard.writeText(text); setCopied('done'); }
    catch { setCopied('failed'); }
    window.setTimeout(() => setCopied('idle'), 2500);
  };

  const btn = 'ctfg-btn ctfg-quiet';
  return (
    <CtfGameScreen
      game={g}
      names={names}
      controls={controls}
      isBot={isBot}
      onAction={dispatch}
      audience={audience}
      onResign={t => dispatch({ type: 'resign', playerId: playerOf(g, t).id })}
      unitType={leagueInfo ? (t, n) => leagueUnitType(leagueInfo, t, n) : undefined}
      turnExtra={sync || canTakeBack || rb || state.rewound || (isLeader && botIds.size > 0 && g.phase === 'playing') ? <>
        {canTakeBack && (
          <button type="button" className={btn} onClick={() => takeBackMoves(true)} title="Back to the start of this activation (or your last dice roll, if you've rolled)">Undo</button>
        )}
        {rb && !rbMine && myTeams.length > 0 && (
          <div role="alert" style={{ margin: '8px 0', padding: '8px 10px', borderRadius: 6, background: '#3d3628', color: '#f2d48f', fontSize: 13, display: 'grid', gap: 6 }}>
            <span>{room.participants.find(p => p.id === rb.by)?.name ?? 'Your opponent'} asks to roll the game back to {rb.label}.</span>
            <span style={{ display: 'flex', gap: 6 }}>
              <button type="button" className="ctfg-btn ctfg-act" onClick={() => answer(true)}>Agree</button>
              <button type="button" className={btn} onClick={() => answer(false)}>Decline</button>
            </span>
          </div>
        )}
        {state.rewound && <p className="ctfg-muted ctfg-small" style={{ margin: '6px 0' }}>Rolled back to {state.rewound}.</p>}
        {sync && (
          <p role="alert" style={{ margin: '0 0 8px', padding: '6px 10px', borderRadius: 6, background: sync === 'lost' ? '#4a2a26' : '#3d3628', color: sync === 'lost' ? '#ffb3a8' : '#f2d48f', fontSize: 13 }}>
            {sync === 'saving' ? 'Connection problem: saving your last move…' : "Your last move didn't reach the server, so the game is back where it was saved. Make it again."}
          </p>
        )}
        {isLeader && botIds.size > 0 && g.phase === 'playing' && (
          <label className="ctfg-muted ctfg-small">Bot speed{' '}
            <select value={speed} onChange={e => setSpeed(Number(e.target.value))} style={{ background: 'var(--sunk)', color: 'var(--ink)', border: '1px solid var(--line)', borderRadius: 4 }}>
              {SPEEDS.map((s2, i) => <option key={s2.label} value={i}>{s2.label}</option>)}
            </select>
          </label>
        )}
      </> : undefined}
      endExtra={league ? <>
        <a className="ctfg-btn ctfg-act" href={`/league/${league.leagueId}`} style={{ textDecoration: 'none' }}>Back to the league</a>
        {isLeader && <button type="button" className={btn} onClick={() => onFinish(resultFor(g, names))}>Back to the room</button>}
      </> : isLeader ? <>
        <button type="button" className="ctfg-btn ctfg-act" onClick={restart}>Rematch</button>
        <button type="button" className={btn} onClick={() => onFinish(resultFor(g, names))}>Back to the room</button>
      </> : undefined}
      summaryExtra={
        <section className="ctfg-panel">
          <div className="ctfg-row" style={{ marginTop: 0 }}>
            <button type="button" className={btn} onClick={() => setShowRules(v => !v)}>{showRules ? 'Hide rules' : 'Rules'}</button>
            <button type="button" className={btn} onClick={copyReplay}>
              {copied === 'done' ? `Copied the ${shareLabel}` : copied === 'failed' ? 'Copy blocked: see below' : `Copy ${shareLabel}`}
            </button>
            <span className="ctfg-muted ctfg-small">Rules v{RULES_VERSION}</span>
          </div>
          {copied === 'failed' && (
            <textarea readOnly value={shareText()} onFocus={e => e.currentTarget.select()}
              style={{ width: '100%', height: 96, marginTop: 8, background: 'var(--sunk)', color: 'var(--ink)', border: '1px solid var(--line)', borderRadius: 6, font: '12px ui-monospace, monospace' }} />
          )}
          {showRules && <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12, marginTop: 8, fontFamily: 'inherit' }}>{describe(g.config)}</pre>}
          {g.phase === 'playing' && myTeams.length > 0 && !state.fromPosition && (
            <div className="ctfg-row" style={{ flexWrap: 'wrap' }}>
              {rbMine ? (
                <>
                  <span className="ctfg-muted ctfg-small">Waiting for {otherPerson ? names[otherPerson] : 'your opponent'} to agree to roll back to {rb!.label}.</span>
                  <button type="button" className={btn} onClick={callOff}>Call it off</button>
                </>
              ) : !rollbackOpen ? (
                <button type="button" className={btn} onClick={() => setRollbackOpen(true)}
                  title="Go back to an earlier point in the game (the other player has to agree)">Request rollback</button>
              ) : (
                <div style={{ display: 'grid', gap: 6, width: '100%' }}>
                  <label className="ctfg-muted ctfg-small" htmlFor="ctf-rollback">Roll the game back to the start of</label>
                  <select id="ctf-rollback" value={chosenPoint?.at ?? ''} onChange={e => setRollbackTo(Number(e.target.value))}
                    style={{ background: 'var(--sunk)', color: 'var(--ink)', border: '1px solid var(--line)', borderRadius: 4, padding: 4 }}>
                    {points.map(pt => <option key={pt.at} value={pt.at}>{pt.label}</option>)}
                  </select>
                  <span style={{ display: 'flex', gap: 6 }}>
                    <button type="button" className="ctfg-btn ctfg-act" disabled={!chosenPoint} onClick={askRollback}>{otherPerson ? `Ask ${names[otherPerson]}` : 'Roll back'}</button>
                    <button type="button" className={btn} onClick={() => { setRollbackOpen(false); setRollbackTo(null); }}>Cancel</button>
                  </span>
                  {otherPerson && <span className="ctfg-muted ctfg-small">Everything after that point is undone if {names[otherPerson]} agrees.</span>}
                </div>
              )}
            </div>
          )}
          {isLeader && !league && (
            <div className="ctfg-row">
              {g.phase === 'playing' && <button type="button" className={btn} onClick={() => setHostTool(hostTool === 'restart' ? 'none' : 'restart')}>Restart</button>}
              <button type="button" className={btn} onClick={() => setHostTool(hostTool === 'load' ? 'none' : 'load')}>Load a game</button>
            </div>
          )}
          {isLeader && hostTool === 'restart' && (
            <div className="ctfg-row ctfg-confirm" role="group" aria-label="Confirm restart">
              <span>Start a new game with the same settings? This one is lost.</span>
              <button type="button" className="ctfg-btn ctfg-act" onClick={restart}>Restart</button>
              <button type="button" className={btn} onClick={() => setHostTool('none')}>Cancel</button>
            </div>
          )}
          {isLeader && hostTool === 'load' && (
            <div style={{ marginTop: 10 }}>
              <label className="ctfg-muted ctfg-small" htmlFor="ctf-load">Paste a replay (Copy replay) or a position from the playground. A replay is played through to its end; everyone in the room sees it.</label>
              <textarea id="ctf-load" value={loadText} onChange={e => setLoadText(e.target.value)}
                style={{ width: '100%', height: 96, marginTop: 6, background: 'var(--sunk)', color: 'var(--ink)', border: '1px solid var(--line)', borderRadius: 6, font: '12px ui-monospace, monospace' }} />
              <div className="ctfg-row">
                <button type="button" className="ctfg-btn ctfg-act" disabled={!loadText.trim()} onClick={load}>Load into this room</button>
                <button type="button" className={btn} onClick={() => { setHostTool('none'); setLoadError(null); }}>Cancel</button>
              </div>
              {loadError && <p style={{ color: 'var(--miss)', fontSize: 12.5, margin: '8px 0 0' }}>{loadError}</p>}
            </div>
          )}
        </section>
      }
    />
  );
}
